import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useAppointmentAlert } from '../../contexts/AppointmentAlertContext';

// ─── helpers for taller map solicitudes ──────────────────────────────────────
const CONSENT_LABELS = {
  no_compartir: 'Sin historial',
  compartir_resumen: 'Resumen',
  compartir_historial_completo: 'Historial completo',
  compartir_fotos_documentos: 'Fotos/documentos',
};

const formatVigencia = (ts) => {
  if (!ts) return null;
  const diff = new Date(ts) - Date.now();
  if (diff <= 0) return 'Expirado';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m restantes`;
  return `${minutes} min restantes`;
};

const URGENCY_CONFIG = {
  normal: { label: 'Normal', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-400' },
  urgente: { label: 'Urgente', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  critico: { label: 'Crítico', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
};

const STATUS_CONFIG = {
  nueva: { label: 'Nueva', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  'en revisión': { label: 'En revisión', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
  respondida: { label: 'Respondida', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  cancelada: { label: 'Cancelada', bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' },
};

const TAB_STATUSES = {
  nuevas: ['nueva'],
  'en_revision': ['en revisión'],
  respondidas: ['respondida'],
  canceladas: ['cancelada'],
};

const TABS = [
  { id: 'todas', label: 'Todas' },
  { id: 'nuevas', label: 'Nuevas' },
  { id: 'en_revision', label: 'En revisión' },
  { id: 'respondidas', label: 'Respondidas' },
  { id: 'canceladas', label: 'Canceladas' },
];

const formatFecha = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Stub modal components
const SolicitudDetailModal = ({ solicitud, citaData, onClose, onAction, saving, userProfile }) => {
  if (!solicitud) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-xl shadow-lg p-6 max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Detalle de Solicitud</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-2"><strong>Tipo:</strong> {solicitud?.request_type}</p>
        <p className="text-sm text-muted-foreground mb-2"><strong>Estado:</strong> {solicitud?.status}</p>
        <p className="text-sm text-muted-foreground mb-2"><strong>Cliente:</strong> {solicitud?.clients?.nombre || '—'}</p>
        {solicitud?.description && <p className="text-sm text-muted-foreground mb-4"><strong>Descripción:</strong> {solicitud?.description}</p>}
        {citaData && (
          <div className="bg-muted/40 rounded p-3 mb-4">
            <p className="text-xs font-semibold text-foreground mb-1">Cita agendada</p>
            <p className="text-xs text-muted-foreground">{citaData?.appointment_date} {citaData?.appointment_time}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {solicitud?.status === 'nueva' && (
            <button onClick={() => onAction('atender', solicitud)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
              <Icon name="PlayCircle" size={12} /> Atender
            </button>
          )}
          <button onClick={() => onAction('abrir_modal_cita', solicitud)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 text-xs font-semibold hover:bg-blue-200 transition-colors disabled:opacity-60">
            <Icon name="Calendar" size={12} /> {solicitud?.converted_to === 'cita' ? 'Reagendar cita' : 'Convertir a cita'}
          </button>
          <button onClick={() => onAction('convertir_recepcion', solicitud)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors disabled:opacity-60">
            <Icon name="ClipboardCheck" size={12} /> Crear recepción
          </button>
          <button onClick={() => onAction('convertir_cotizacion', solicitud)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors disabled:opacity-60">
            <Icon name="FileText" size={12} /> Crear cotización
          </button>
          {solicitud?.status !== 'cancelada' && (
            <button onClick={() => onAction('cancelar', solicitud)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-60">
              <Icon name="XCircle" size={12} /> Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ConvertirCitaModal = ({ solicitud, existingCita, onClose, onGuardar, saving }) => {
  const [fecha, setFecha] = React.useState(existingCita?.appointment_date || '');
  const [hora, setHora] = React.useState(existingCita?.appointment_time || '');
  const [notas, setNotas] = React.useState(existingCita?.notes || '');
  const isReagendar = !!existingCita;

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!fecha || !hora) return;
    onGuardar({ fecha, hora, notas });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">{isReagendar ? 'Reagendar cita' : 'Agendar cita'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Fecha</label>
            <input type="date" value={fecha} onChange={e => setFecha(e?.target?.value)} required className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Hora</label>
            <input type="time" value={hora} onChange={e => setHora(e?.target?.value)} required className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Notas (opcional)</label>
            <textarea value={notas} onChange={e => setNotas(e?.target?.value)} rows={3} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors disabled:opacity-60">Cancelar</button>
            <button type="submit" disabled={saving || !fecha || !hora} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
              {saving ? 'Guardando...' : isReagendar ? 'Reagendar' : 'Agendar cita'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── SolicitudChatModal (taller side) ────────────────────────────────────────
const SolicitudChatModal = ({ solicitud, userProfile, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(true);
  const [msgText, setMsgText] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const messagesEndRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!solicitud?.id) return;
    try {
      const { data } = await supabase
        ?.from('solicitud_messages')
        ?.select('id, sender_type, sender_name, body, created_at')
        ?.eq('solicitud_id', solicitud?.id)
        ?.order('created_at', { ascending: true });
      setMessages(data || []);
    } catch (err) {
      console.error('[SolicitudesManagement] Error loading messages:', err);
    } finally {
      setMsgLoading(false);
    }
  }, [solicitud?.id]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useEffect(() => {
    if (!solicitud?.id) return;
    const channel = supabase
      ?.channel(`taller_msgs_${solicitud?.id}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'solicitud_messages',
        filter: `solicitud_id=eq.${solicitud?.id}`,
      }, (payload) => {
        setMessages(prev => [...prev, payload?.new]);
      })
      ?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [solicitud?.id]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = msgText?.trim();
    if (!text || msgSending) return;
    setMsgSending(true);
    try {
      await supabase?.from('solicitud_messages')?.insert({
        company_id: solicitud?.company_id,
        solicitud_id: solicitud?.id,
        client_id: solicitud?.client_id,
        sender_type: 'taller',
        sender_name: userProfile?.full_name || userProfile?.email || 'Taller',
        body: text,
      });
      setMsgText('');
    } catch (err) {
      console.error('[SolicitudesManagement] Error sending message:', err);
    } finally {
      setMsgSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts)?.toLocaleString('es-MX', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-xl shadow-lg flex flex-col w-full max-w-md mx-4" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground">{solicitud?.request_type}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {solicitud?.clients?.nombre || '—'} · Conversación
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
          {msgLoading ? (
            <div className="flex items-center justify-center h-24">
              <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            </div>
          ) : messages?.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">
              Sin mensajes aún. Escribe para iniciar la conversación con el cliente.
            </p>
          ) : (
            messages?.map((msg) => {
              const isTaller = msg?.sender_type === 'taller';
              return (
                <div key={msg?.id} className={`flex flex-col ${isTaller ? 'items-end' : 'items-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                    isTaller
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-muted text-foreground rounded-bl-sm'
                  }`}>
                    {msg?.body}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5 px-1">
                    {msg?.sender_name} · {formatTime(msg?.created_at)}
                  </p>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-border p-4 flex-shrink-0">
          <div className="flex gap-2">
            <textarea
              value={msgText}
              onChange={(e) => setMsgText(e?.target?.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Escribe tu respuesta al cliente..."
              className="flex-1 bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
            <button
              onClick={handleSend}
              disabled={!msgText?.trim() || msgSending}
              className="flex items-center justify-center px-3 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground rounded-lg transition-colors self-end"
            >
              {msgSending
                ? <Icon name="Loader2" size={16} className="animate-spin" />
                : <Icon name="Send" size={16} />
              }
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Enter para enviar · Shift+Enter para nueva línea</p>
        </div>
      </div>
    </div>
  );
};

// ─── SolicitudRow ─────────────────────────────────────────────────────────────
const SolicitudRow = ({ solicitud, onVerDetalle, onAction, saving, onChat }) => {
  const isNueva = solicitud?.status === 'nueva';
  const isUnread = !solicitud?.read_at;
  const urg = URGENCY_CONFIG?.[solicitud?.urgency] || URGENCY_CONFIG?.normal;
  const sta = STATUS_CONFIG?.[solicitud?.status] || STATUS_CONFIG?.nueva;
  const isMapaPortal = solicitud?.origen === 'mapa_portal_cliente';
  const vigencia = isMapaPortal && !solicitud?.es_taller_confianza ? formatVigencia(solicitud?.acceso_vigencia) : null;

  return (
    <div className={`
      relative rounded-lg border p-4 transition-all
      ${isNueva && isUnread
        ? 'border-orange-300 bg-orange-50/50 animate-pulse-subtle shadow-sm shadow-orange-100'
        : 'border-border bg-card hover:bg-muted/20'
      }
    `}>
      {isNueva && isUnread && (
        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow">!</span>
      )}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        {/* Left info */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            {isNueva && isUnread && (
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-ping flex-shrink-0" />
            )}
            <p className="text-sm font-semibold text-foreground">{solicitud?.request_type}</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${sta?.bg} ${sta?.text} ${sta?.border}`}>
              {sta?.label}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${urg?.bg} ${urg?.text} ${urg?.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${urg?.dot}`} />
              {urg?.label}
            </span>
            {/* Origin badge */}
            {isMapaPortal ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                <Icon name="MapPin" size={9} />
                Mapa Portal Cliente
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                <Icon name="Globe" size={9} />
                Portal Cliente
              </span>
            )}
            {/* Trusted workshop badge */}
            {isMapaPortal && solicitud?.es_taller_confianza && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">
                <Icon name="ShieldCheck" size={9} />
                Taller de confianza
              </span>
            )}
            {isMapaPortal && !solicitud?.es_taller_confianza && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                <Icon name="Timer" size={9} />
                Acceso temporal
              </span>
            )}
            {solicitud?.converted_to && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                <Icon name="CheckCircle2" size={9} />
                → {solicitud?.converted_to}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Icon name="User" size={11} />
              {solicitud?.clients?.nombre || '—'}
            </span>
            {solicitud?.vehicles && (
              <span className="flex items-center gap-1">
                <Icon name="Car" size={11} />
                {solicitud?.vehicles?.marca} {solicitud?.vehicles?.modelo} {solicitud?.vehicles?.year}
                {solicitud?.vehicles?.placas ? ` · ${solicitud?.vehicles?.placas}` : ''}
              </span>
            )}
            {isMapaPortal && solicitud?.taller_nombre && (
              <span className="flex items-center gap-1">
                <Icon name="Wrench" size={11} />
                {solicitud?.taller_nombre}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Icon name="Clock" size={11} />
              {formatFecha(solicitud?.created_at)}
            </span>
          </div>

          {/* Taller map extra info row */}
          {isMapaPortal && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground bg-muted/30 rounded px-2 py-1.5">
              {solicitud?.consent_historial && (
                <span className="flex items-center gap-1">
                  <Icon name="Database" size={10} />
                  <span className="font-medium">Datos:</span> {CONSENT_LABELS?.[solicitud?.consent_historial] || solicitud?.consent_historial}
                </span>
              )}
              {!solicitud?.es_taller_confianza && vigencia && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Icon name="Timer" size={10} />
                  <span className="font-medium">Vigencia:</span> {vigencia}
                </span>
              )}
              {solicitud?.es_taller_confianza && (
                <span className="flex items-center gap-1 text-green-600">
                  <Icon name="ShieldCheck" size={10} />
                  Acceso persistente autorizado
                </span>
              )}
            </div>
          )}

          {solicitud?.description && (
            <p className="text-xs text-foreground/70 line-clamp-1 bg-muted/40 rounded px-2 py-1">
              {solicitud?.description}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-1.5 sm:flex-col sm:items-end">
          <button
            onClick={() => onVerDetalle(solicitud)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
          >
            <Icon name="Eye" size={12} />
            Ver detalle
          </button>
          <button
            onClick={() => onChat(solicitud)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
          >
            <Icon name="MessageSquare" size={12} />
            Mensajes
          </button>
          {!solicitud?.read_at && (
            <button
              onClick={() => onAction('marcar_leida', solicitud)}
              disabled={saving}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors disabled:opacity-60"
            >
              <Icon name="CheckCheck" size={12} />
              Marcar leída
            </button>
          )}
          {solicitud?.status === 'nueva' && (
            <button
              onClick={() => onAction('atender', solicitud)}
              disabled={saving}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              <Icon name="PlayCircle" size={12} />
              Atender
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const SolicitudesManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [detalle, setDetalle] = useState(null);
  const [detalleCita, setDetalleCita] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  // Modal de cita
  const [citaModal, setCitaModal] = useState(null); // { solicitud, existingCita }
  const [solicitudChatModal, setSolicitudChatModal] = useState(null);
  const { userProfile } = useAuth();
  const { showAppointmentAlert } = useAppointmentAlert();
  const navigate = useNavigate();

  const companyId = userProfile?.company_id;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const cargarSolicitudes = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        ?.from('service_requests')
        ?.select(`
          id, company_id, request_type, description, urgency, status, notes,
          created_at, attended_at, read_at, origen, converted_to, converted_id,
          client_id, vehicle_id,
          taller_nombre, taller_id, es_taller_confianza, consent_historial, acceso_vigencia,
          clients(nombre, telefono),
          vehicles(marca, modelo, year, placas)
        `)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false })
        ?.limit(200);

      if (fetchErr) {
        console.error('[SolicitudesManagement] Error al cargar solicitudes:', fetchErr);
        setError('No se pudieron cargar las solicitudes. Intenta de nuevo.');
        setSolicitudes([]);
        return;
      }
      setSolicitudes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[SolicitudesManagement] Excepción al cargar solicitudes:', err);
      setError('Error inesperado al cargar solicitudes.');
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Real-time subscription
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    cargarSolicitudes();

    let channel = supabase
      ?.channel(`solicitudes_mgmt_${companyId}`)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'service_requests',
        filter: `company_id=eq.${companyId}`,
      }, () => { cargarSolicitudes(); })
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [companyId, cargarSolicitudes]);

  // ── Cargar datos de cita cuando se abre el detalle ──────────────────────────
  const cargarCita = useCallback(async (citaId) => {
    if (!citaId) { setDetalleCita(null); return; }
    try {
      const { data, error: citaErr } = await supabase
        ?.from('client_appointments')
        ?.select('id, appointment_date, appointment_time, notes, status, service_type')
        ?.eq('id', citaId)
        ?.maybeSingle();
      if (citaErr) {
        console.error('[SolicitudesManagement] Error al cargar cita:', citaErr);
        setDetalleCita(null);
        return;
      }
      setDetalleCita(data || null);
    } catch (err) {
      console.error('[SolicitudesManagement] Excepción al cargar cita:', err);
      setDetalleCita(null);
    }
  }, []);

  // Cuando se abre el detalle, cargar cita si existe
  useEffect(() => {
    if (detalle?.converted_id && detalle?.converted_to === 'cita') {
      cargarCita(detalle?.converted_id);
    } else {
      setDetalleCita(null);
    }
  }, [detalle, cargarCita]);

  // ── handleAction ────────────────────────────────────────────────────────────
  const handleAction = async (action, solicitud) => {
    // Abrir modal de cita (no es async)
    if (action === 'abrir_modal_cita') {
      let existingCita = null;
      if (solicitud?.converted_id && solicitud?.converted_to === 'cita') {
        try {
          const { data } = await supabase
            ?.from('client_appointments')
            ?.select('id, appointment_date, appointment_time, notes, service_type')
            ?.eq('id', solicitud?.converted_id)
            ?.maybeSingle();
          existingCita = data || null;
        } catch (err) {
          console.error('[SolicitudesManagement] Error al cargar cita existente:', err);
        }
      }
      setCitaModal({ solicitud, existingCita });
      return;
    }

    setSaving(true);
    try {
      if (action === 'marcar_leida') {
        await supabase?.from('service_requests')?.update({
          read_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', solicitud?.id);
        showToast('Solicitud marcada como leída');
      }

      else if (action === 'atender') {
        await supabase?.from('service_requests')?.update({
          status: 'en revisión',
          attended_at: new Date()?.toISOString(),
          read_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', solicitud?.id);
        showToast('Solicitud marcada en revisión');
      }

      else if (action === 'convertir_recepcion') {
        await supabase?.from('service_requests')?.update({
          converted_to: 'recepcion',
          read_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', solicitud?.id);
        navigate('/vehicle-reception', {
          state: {
            fromSolicitud: true,
            solicitudId: solicitud?.id,
            clientId: solicitud?.client_id,
            vehicleId: solicitud?.vehicle_id,
            requestType: solicitud?.request_type,
            description: solicitud?.description,
            clientNombre: solicitud?.clients?.nombre,
            clientTelefono: solicitud?.clients?.telefono,
            vehicleMarca: solicitud?.vehicles?.marca,
            vehicleModelo: solicitud?.vehicles?.modelo,
            vehiclePlacas: solicitud?.vehicles?.placas,
          }
        });
        return;
      }

      else if (action === 'convertir_cotizacion') {
        await supabase?.from('service_requests')?.update({
          converted_to: 'cotizacion',
          read_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', solicitud?.id);
        navigate('/quotation-management', {
          state: {
            fromSolicitud: true,
            solicitudId: solicitud?.id,
            clientId: solicitud?.client_id,
            vehicleId: solicitud?.vehicle_id,
            requestType: solicitud?.request_type,
            description: solicitud?.description,
            clientNombre: solicitud?.clients?.nombre,
            clientTelefono: solicitud?.clients?.telefono,
            vehicleMarca: solicitud?.vehicles?.marca,
            vehicleModelo: solicitud?.vehicles?.modelo,
            vehicleYear: solicitud?.vehicles?.year,
            vehiclePlacas: solicitud?.vehicles?.placas,
            vin: solicitud?.vehicles?.vin,
            urgency: solicitud?.urgency,
          }
        });
        return;
      }

      else if (action === 'cancelar') {
        await supabase?.from('service_requests')?.update({
          status: 'cancelada',
          updated_at: new Date()?.toISOString(),
        })?.eq('id', solicitud?.id);
        showToast('Solicitud cancelada');
      }

      await cargarSolicitudes();
    } catch (err) {
      console.error('Error en acción:', err);
      showToast('Error al procesar la acción', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Guardar cita (desde modal) ───────────────────────────────────────────────
  const handleGuardarCita = async ({ fecha, hora, notas }) => {
    if (!citaModal?.solicitud) return;
    const solicitud = citaModal?.solicitud;
    let existingCita = citaModal?.existingCita;
    const isReagendar = !!existingCita;

    setSaving(true);
    try {
      const now = new Date()?.toISOString();
      let citaId = existingCita?.id || null;

      if (isReagendar && citaId) {
        // UPDATE cita existente
        const { error: updateErr } = await supabase
          ?.from('client_appointments')
          ?.update({
            appointment_date: fecha,
            appointment_time: hora,
            notes: notas || null,
            status: 'pendiente',
            updated_at: now,
          })
          ?.eq('id', citaId);

        if (updateErr) {
          console.error('[SolicitudesManagement] Error al reagendar cita:', updateErr);
          throw updateErr;
        }
      } else {
        // INSERT nueva cita — columnas correctas según migración 20260312200000_client_portal.sql
        const { data: nuevaCita, error: insertErr } = await supabase
          ?.from('client_appointments')
          ?.insert({
            company_id: companyId,
            client_id: solicitud?.client_id,
            vehicle_id: solicitud?.vehicle_id || null,
            service_type: solicitud?.request_type,   // NOT NULL en la tabla
            appointment_date: fecha,                  // tipo date
            appointment_time: hora,                   // tipo time
            notes: notas || null,
            status: 'pendiente',
          })
          ?.select('id')
          ?.single();

        if (insertErr) {
          console.error('[SolicitudesManagement] Error al insertar cita:', insertErr);
          throw insertErr;
        }
        citaId = nuevaCita?.id || null;
      }

      // Actualizar service_request
      const { error: srErr } = await supabase
        ?.from('service_requests')
        ?.update({
          converted_to: 'cita',
          converted_id: citaId,
          status: 'respondida',
          attended_at: now,           // tiempo de respuesta
          read_at: now,
          updated_at: now,
        })
        ?.eq('id', solicitud?.id);

      if (srErr) {
        console.error('[SolicitudesManagement] Error al actualizar solicitud:', srErr);
        throw srErr;
      }

      // ── CRM sync: insertar calendar_event ──────────────────────────────────
      try {
        const startDatetime = `${fecha}T${hora}:00`;
        await supabase
          ?.from('calendar_events')
          ?.insert({
            company_id: companyId,
            title: `Cita: ${solicitud?.request_type}`,
            description: `Cliente: ${solicitud?.clients?.nombre || '—'} | ${
              solicitud?.vehicles
                ? `${solicitud?.vehicles?.marca || ''} ${solicitud?.vehicles?.modelo || ''} ${solicitud?.vehicles?.year || ''}`
                : 'Sin vehículo'
            }${notas ? ` | ${notas}` : ''}`,
            related_type: 'followup',
            related_id: citaId,
            client_id: solicitud?.client_id || null,
            vehicle_id: solicitud?.vehicle_id || null,
            start_datetime: startDatetime,
            status: 'scheduled',
            color: '#3B82F6',
            created_by: userProfile?.id || null,
          });
      } catch (crmErr) {
        // CRM sync no es crítico — loguear pero no bloquear
        console.warn('[SolicitudesManagement] CRM calendar_event no se pudo crear:', crmErr);
      }

      // ── Alerta visual de cita ──────────────────────────────────────────────
      const vehicleLabel = solicitud?.vehicles
        ? `${solicitud?.vehicles?.marca || ''} ${solicitud?.vehicles?.modelo || ''} ${solicitud?.vehicles?.year ? `(${solicitud?.vehicles?.year})` : ''}`?.trim()
        : null;
      showAppointmentAlert({
        tipo: isReagendar ? 'reagendada' : 'nueva',
        origen: 'Solicitudes',
        cliente: solicitud?.clients?.nombre || '—',
        telefono: solicitud?.clients?.telefono || null,
        vehiculo: vehicleLabel || null,
        placa: solicitud?.vehicles?.placas || null,
        servicio: solicitud?.request_type,
        fecha,
        hora,
        notas: notas || null,
        usuario: userProfile?.full_name || userProfile?.email || 'Sistema',
      });

      // Cerrar modal y refrescar
      setCitaModal(null);
      await cargarSolicitudes();

      // Refrescar detalle con datos actualizados
      if (solicitud?.id) {
        try {
          const { data: updatedSolicitud } = await supabase
            ?.from('service_requests')
            ?.select(`
              id, request_type, description, urgency, status, notes,
              created_at, attended_at, read_at, origen, converted_to, converted_id,
              client_id, vehicle_id,
              taller_nombre, taller_id, es_taller_confianza, consent_historial, acceso_vigencia,
              clients(nombre, telefono),
              vehicles(marca, modelo, year, placas)
            `)
            ?.eq('id', solicitud?.id)
            ?.maybeSingle();
          if (updatedSolicitud) {
            setDetalle(updatedSolicitud);
            if (citaId) await cargarCita(citaId);
          }
        } catch (refreshErr) {
          console.warn('[SolicitudesManagement] No se pudo refrescar el detalle:', refreshErr);
        }
      }

      showToast(isReagendar ? 'Cita reagendada correctamente' : 'Solicitud convertida a cita');
    } catch (err) {
      console.error('[SolicitudesManagement] Error al guardar cita:', err);
      showToast(err?.message || 'Error al guardar la cita', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Filter by tab
  const solicitudesFiltradas = (() => {
    if (!Array.isArray(solicitudes)) return [];
    const statuses = TAB_STATUSES?.[activeTab];
    if (!statuses) return solicitudes; // 'todas' — no filter
    return solicitudes?.filter(s => statuses?.includes(s?.status));
  })();

  const nuevasCount = Array.isArray(solicitudes)
    ? solicitudes?.filter(s => TAB_STATUSES?.nuevas?.includes(s?.status) && !s?.read_at)?.length || 0
    : 0;

  return (
    <div className="min-h-screen bg-background font-body">
      <MainSidebar isCollapsed={isSidebarCollapsed} />
      <main className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div className="flex items-center gap-3 min-w-0 pl-10 lg:pl-0">
                <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name="ClipboardList" size={22} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-heading font-semibold text-foreground leading-tight">Solicitudes</h1>
                  <p className="text-sm text-muted-foreground mt-1">Solicitudes del Portal Cliente</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {nuevasCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-ping flex-shrink-0" />
                  <span className="text-sm text-orange-700 font-medium">
                    {nuevasCount} nueva{nuevasCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="px-4 md:px-6 lg:px-8 py-6">
          <div className="max-w-5xl mx-auto space-y-5">

            {/* Tabs */}
            <div className="flex items-center gap-1 bg-muted rounded-xl p-1 w-fit">
              {TABS?.map(tab => {
                const statuses = TAB_STATUSES?.[tab?.id];
                const count = !statuses
                  ? solicitudes?.length
                  : solicitudes?.filter(s => statuses?.includes(s?.status))?.length;
                const isNuevasTab = tab?.id === 'nuevas';

                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab?.id
                        ? 'bg-card shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab?.label}
                    {count > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold ${
                        isNuevasTab && nuevasCount > 0
                          ? 'bg-red-500' : 'bg-muted-foreground/20'
                      }`}>
                        {isNuevasTab ? nuevasCount || count : count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Alert banner */}
            {nuevasCount > 0 && (
              <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-ping flex-shrink-0" />
                <p className="text-sm text-orange-800 font-medium">
                  {nuevasCount === 1
                    ? '1 solicitud nueva sin atender del Portal Cliente'
                    : `${nuevasCount} solicitudes nuevas sin atender del Portal Cliente`}
                </p>
              </div>
            )}

            {/* List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={28} className="text-primary animate-spin" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 bg-card rounded-xl border border-border gap-3">
                <Icon name="AlertCircle" size={36} className="text-red-400" />
                <p className="text-base font-medium text-foreground">{error}</p>
                <button
                  onClick={cargarSolicitudes}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Icon name="RefreshCw" size={14} />
                  Reintentar
                </button>
              </div>
            ) : solicitudesFiltradas?.length === 0 ? (
              <div className="text-center py-16 bg-card rounded-xl border border-border">
                <Icon name="ClipboardList" size={44} className="text-muted-foreground mx-auto mb-3 opacity-40" />
                <p className="text-base font-medium text-foreground mb-1">No hay solicitudes disponibles</p>
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'todas' ? 'No hay solicitudes registradas aún'
                    : `No hay solicitudes en esta categoría`}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {solicitudesFiltradas?.map(s => s?.id ? (
                  <SolicitudRow
                    key={s?.id}
                    solicitud={s}
                    onVerDetalle={setDetalle}
                    onAction={handleAction}
                    saving={saving}
                    onChat={setSolicitudChatModal}
                  />
                ) : null)}
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Detail Modal */}
      {detalle && (
        <SolicitudDetailModal
          solicitud={detalle}
          citaData={detalleCita}
          onClose={() => { setDetalle(null); setDetalleCita(null); }}
          onAction={handleAction}
          saving={saving}
          userProfile={userProfile}
        />
      )}
      {/* Modal Convertir/Reagendar Cita */}
      {citaModal && (
        <ConvertirCitaModal
          solicitud={citaModal?.solicitud}
          existingCita={citaModal?.existingCita}
          onClose={() => setCitaModal(null)}
          onGuardar={handleGuardarCita}
          saving={saving}
        />
      )}
      {/* Chat Modal */}
      {solicitudChatModal && (
        <SolicitudChatModal
          solicitud={solicitudChatModal}
          userProfile={userProfile}
          onClose={() => setSolicitudChatModal(null)}
        />
      )}
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
          toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={18} />
          <span>{toast?.message}</span>
        </div>
      )}
    </div>
  );
};

export default SolicitudesManagement;
